/**
 * LOS ESTADOS DE UN CHAT — un solo sitio, tres selectores y un badge.
 *
 * POR QUÉ EXISTE ESTE MÓDULO (9-sep-2026)
 * ───────────────────────────────────────
 * Había TRES listas de `<option>` escritas a mano —el filtro del inbox, el selector de
 * la tarjeta y el del detalle— más un `STATUS_CONFIG` con los colores que ya contenía
 * los ocho estados y que nadie recorría. Las tres listas ya habían divergido:
 *
 *   · el selector de cada chat ofrecía los 8;
 *   · el filtro del inbox ofrecía 4 (`nuevo`, `contactado`, `en_proceso`, `cerrado`).
 *
 * Resultado medido en producción: de los 5 estados realmente en uso, el filtro no
 * alcanzaba 3. Había 2 chats en `agendado` y 1 en `perdido` invisibles a su propio
 * filtro — se les podía poner el estado y luego no se podían encontrar por él.
 *
 * EL GUION BAJO, Y POR QUÉ SE CAMBIÓ
 * ──────────────────────────────────
 * El front escribía `'sin-estado'` con guion y el backend `'sin_estado'` con guion bajo
 * (`botOnMessage.js:107`, `botOnMessageOrg.js:117`, `onIncomingMessageV2.js:333`). Los
 * otros siete estados usan guion bajo (`en_proceso`), así que la excepción era el guion.
 *
 * Se unifica en `sin_estado`. El momento es barato y no volverá a serlo: medido el
 * 9-sep-2026, NINGUNO de los 208 chats de los 18 tenants tenía ninguna de las dos
 * formas —el camino vivo (`messageRouter.js:566`) escribe `'nuevo'`— así que no hay
 * dato que migrar. Y viene un bloque de reconciliación de vocabularios de estados
 * (`businessMetricsCollector`, `FunnelChart`, `conversionTrackerHelper`, que hablan de
 * `interesado` / `closed_won` / `closed_lost`); heredar la excepción allí habría salido
 * mucho más caro que renombrar una clave de i18n hoy.
 */

/** El estado de quien no tiene estado. Es un VALOR, no la ausencia del campo. */
export const SIN_ESTADO = 'sin_estado';

/**
 * Los ocho estados, EN EL ORDEN DEL EMBUDO.
 *
 * El orden importa: es el que ven los tres selectores, y va de "aún no lo he tocado" a
 * "esto ya terminó". `sin_estado` abre porque es el punto de partida.
 *
 * La etiqueta NO vive aquí: sale de `locales/{es,en,pt}/chats.json` bajo
 * `statuses.<codigo>`, que ya tiene los ocho traducidos. Meter texto en español en este
 * módulo rompería los otros dos idiomas en silencio.
 */
export const ESTADOS_DE_CHAT = [
  { codigo: SIN_ESTADO,   color: '#9CA3AF', icono: '⚫', embudo: null },
  { codigo: 'nuevo',      color: '#10B981', icono: '🟢', embudo: 'etapa' },
  { codigo: 'contactado', color: '#3B82F6', icono: '🔵', embudo: 'etapa' },
  { codigo: 'en_proceso', color: '#F59E0B', icono: '🟡', embudo: 'etapa' },
  { codigo: 'agendado',   color: '#8B5CF6', icono: '🟣', embudo: 'etapa' },
  { codigo: 'ganado',     color: '#10B981', icono: '✅', embudo: 'salida' },
  { codigo: 'perdido',    color: '#EF4444', icono: '❌', embudo: 'salida' },
  { codigo: 'cerrado',    color: '#6B7280', icono: '⚪', embudo: 'salida' },
];

/**
 * EL EMBUDO — el mismo vocabulario que la pantalla, no uno paralelo.
 *
 * Hasta el 10-sep-2026 las métricas hablaban de `interesado`, `closed_won` y
 * `closed_lost`. Medido: esos tres valores no aparecen en NINGUNO de los 208 chats ni
 * en el historial de estados. Eran un vocabulario que no describía ningún dato.
 *
 * `embudo` clasifica cada estado en tres papeles, y los tres importan:
 *   · `'etapa'`  — se avanza por ellas, y entre etapas consecutivas hay una tasa;
 *   · `'salida'` — la conversación terminó. NO son etapas: nadie "pasa por" ganado;
 *   · `null`     — fuera del embudo.
 *
 * POR QUÉ `en_proceso` OCUPA EL HUECO DE `interesado`: es literalmente eso, y es el
 * segundo valor más frecuente en los datos (5 chats). El gráfico lo tiraba en silencio,
 * porque `FunnelChart` filtraba por una lista fija y descartaba lo que no reconocía.
 *
 * POR QUÉ `sin_estado` QUEDA FUERA: no es una etapa, es la ausencia de clasificación.
 * Como etapa inicial inflaría el denominador con chats que nadie ha mirado todavía.
 *
 * POR QUÉ `cerrado` ES SALIDA NEUTRA y no etapa: significa "esto terminó y no fue ni
 * venta ni pérdida" —spam, consulta resuelta, contacto equivocado—. Se pinta como
 * desenlace pero NO entra en la tasa `agendado → ganado`, así que no ensucia la
 * conversión en ninguna dirección.
 *
 * A VIGILAR dentro de un mes: si `cerrado` crece mucho más rápido que `ganado` y
 * `perdido` juntos, significa que el equipo lo está usando como "ya terminé con este"
 * en vez de marcar el desenlace real — y entonces el embudo SUBCUENTA ventas. La
 * proporción entre los tres es el indicador, no el valor absoluto de ninguno.
 */
export const ETAPAS_DEL_EMBUDO = ESTADOS_DE_CHAT
  .filter((e) => e.embudo === 'etapa')
  .map((e) => e.codigo);

export const SALIDAS_DEL_EMBUDO = ESTADOS_DE_CHAT
  .filter((e) => e.embudo === 'salida')
  .map((e) => e.codigo);

/**
 * Lo que el gráfico del embudo pinta, EN ORDEN. El orden es contrato, no decoración:
 * `FunnelChart` lo usa para ordenar las barras y para decidir qué claves pinta —
 * cualquier estado que no esté aquí desaparece del gráfico sin un solo error.
 */
export const ESTADOS_DEL_EMBUDO = [...ETAPAS_DEL_EMBUDO, ...SALIDAS_DEL_EMBUDO];

/** El desenlace que cuenta como conversión. Los otros dos no cierran ninguna tasa. */
export const SALIDA_GANADORA = 'ganado';

/**
 * Las tasas entre etapas consecutivas, derivadas —no escritas a mano— para que no
 * puedan desalinearse con `ETAPAS_DEL_EMBUDO`. La última etapa convierte hacia
 * `SALIDA_GANADORA`. Las claves (`'nuevo->contactado'`) son el contrato entre el
 * collector del servidor y `FunnelConversionFlow`.
 */
export const CONVERSIONES_DEL_EMBUDO = ETAPAS_DEL_EMBUDO.map((desde, i) => {
  const hasta = ETAPAS_DEL_EMBUDO[i + 1] || SALIDA_GANADORA;
  return { desde, hasta, clave: `${desde}->${hasta}` };
});

/** La clave de i18n de un estado. Un solo sitio construye esta cadena. */
export const claveDeEtiqueta = (codigo) => `statuses.${codigo}`;

/**
 * El estado EFECTIVO de un chat: lo que la pantalla debe tratar como su estado.
 *
 * Normaliza campo ausente, `null` y cadena vacía a `sin_estado`. Se usa TANTO en el
 * badge de la tarjeta COMO en el filtro, y eso no es un detalle: si el badge dijera
 * "Sin estado" y el filtro por "Sin estado" no lo encontrara, el usuario vería una
 * pantalla que se contradice a sí misma.
 *
 * Hoy los 208 chats tienen `estado` (medido), así que esto no cambia nada — existe para
 * que siga sin cambiar nada el día que un camino nuevo cree un chat sin él.
 */
export function estadoEfectivo(chat) {
  const v = chat?.estado;
  return typeof v === 'string' && v.trim() !== '' ? v : SIN_ESTADO;
}

/** Colores e icono de un estado, con caída segura a `sin_estado` si llega uno raro. */
export function configDeEstado(codigo) {
  return (
    ESTADOS_DE_CHAT.find((e) => e.codigo === codigo) ||
    ESTADOS_DE_CHAT.find((e) => e.codigo === SIN_ESTADO)
  );
}
